import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Grid,
  CircularProgress
} from '@material-ui/core'
import { useStyle } from './Style'
import QuotationResults from './QuotationResults'

const todayISO = () => new Date().toISOString().split('T')[0]

const MAX_DEPENDANTS = 3
// First dependant is always Principal; these are the options for the rest.
const RELATIONSHIP_OPTIONS = [
  'Spouse',
  'Child',
  'Parent',
  'Parent_In_Law',
  'Extended'
]
// Benefits and add-ons are picked PER dependant.
const newDependant = (relationship = 'Child') => ({
  relationship,
  date_of_birth: '',
  benefits: [],
  add_ons: []
})

function ErrorMessage({ children }) {
  return (
    <div
      style={{
        marginTop: 16,
        padding: '12px 16px',
        borderRadius: 4,
        backgroundColor: 'rgb(253, 236, 234)',
        color: 'rgb(97, 26, 21)',
        fontSize: 14
      }}
    >
      {children}
    </div>
  )
}

function QuotationDialog(props) {
  const classes = useStyle()
  const {
    open,
    onClose,
    currentProduct,
    quotationResults,
    quotationLoader,
    quotationError,
    onEvaluate
  } = props

  // Tracks Package.code, not guid — some imported packages (e.g. via AI-draft
  // import) have been found on staging without a guid set, which collapses
  // every MenuItem's value to `undefined` and breaks MUI's selection tracking.
  // `code` is a required field on every package, so it's a reliable key.
  const [selectedPackageCode, setSelectedPackageCode] = useState('')
  const [dependants, setDependants] = useState([newDependant('Principal')])
  const [evaluationDate, setEvaluationDate] = useState(todayISO())
  const [factRows, setFactRows] = useState([])

  useEffect(() => {
    if (open) {
      setEvaluationDate(todayISO())
      setSelectedPackageCode('')
      setDependants([newDependant('Principal')])
      setFactRows([])
    }
  }, [open])

  const packages =
    currentProduct && currentProduct.packages ? currentProduct.packages : []
  const addons =
    currentProduct && currentProduct.add_ons ? currentProduct.add_ons : []

  const selectedPkg = packages.find(p => p.code === selectedPackageCode) || null
  const pkgBenefits =
    selectedPkg && selectedPkg.benefits ? selectedPkg.benefits : []

  const handlePackageChange = e => {
    setSelectedPackageCode(e.target.value)
    // Benefits are package-scoped — clear each dependant's selection on switch.
    setDependants(prev => prev.map(d => ({ ...d, benefits: [] })))
  }

  const addDependant = () => {
    setDependants(prev =>
      prev.length >= MAX_DEPENDANTS ? prev : [...prev, newDependant()]
    )
  }

  const removeDependant = index => {
    setDependants(prev => prev.filter((_, i) => i !== index))
  }

  const updateDependant = (index, field, value) => {
    setDependants(prev =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d))
    )
  }

  // Toggle a benefit/add-on guid within a single dependant's selection.
  const toggleDependantItem = (index, field, guid) => {
    setDependants(prev =>
      prev.map((d, i) => {
        if (i !== index) return d
        const list = d[field] || []
        return {
          ...d,
          [field]: list.includes(guid)
            ? list.filter(g => g !== guid)
            : [...list, guid]
        }
      })
    )
  }

  const addFactRow = () => {
    setFactRows(prev => [...prev, { key: '', value: '' }])
  }

  const removeFactRow = index => {
    setFactRows(prev => prev.filter((_, i) => i !== index))
  }

  const updateFactRow = (index, field, value) => {
    setFactRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    )
  }

  // A fact value typed as text needs to become the right JS type — a rating
  // node's base_formula/base_value_ref, and a table segment's expression,
  // compare against numbers/booleans, not strings.
  const coerceFactValue = raw => {
    if (raw === '') return ''
    if (raw === 'true') return true
    if (raw === 'false') return false
    const asNumber = Number(raw)
    return raw.trim() !== '' && !Number.isNaN(asNumber) ? asNumber : raw
  }

  const handleSubmit = () => {
    const deps = dependants.map(d => {
      const dep = {
        relationship: d.relationship,
        date_of_birth: d.date_of_birth
      }
      // Backend's DependantInputDto now takes a unified
      // DependantBenefitSelectionDto[] ({ benefit_guid, chosen_amount_in_cents? })
      // instead of a bare benefit-guid string[] — this UI doesn't offer a
      // member-chosen cover amount input yet, so chosen_amount_in_cents is
      // simply omitted (it's optional on the DTO).
      if ((d.benefits || []).length > 0) {
        dep.benefits = d.benefits.map(benefitGuid => ({
          benefit_guid: benefitGuid
        }))
      }
      // add_ons is still a bare guid string[] on the backend DTO — unchanged.
      if ((d.add_ons || []).length > 0) dep.add_ons = d.add_ons
      return dep
    })

    // Reserved `_`-prefixed keys are rejected by the API; drop them client-side
    // rather than surfacing a confusing 400 for a fact name typo.
    const facts = {}
    factRows.forEach(({ key, value }) => {
      const trimmedKey = key.trim()
      if (trimmedKey && !trimmedKey.startsWith('_')) {
        facts[trimmedKey] = coerceFactValue(value)
      }
    })

    onEvaluate({
      productGuid: currentProduct.guid,
      payload: {
        // Quote-level facts are read as top-level body fields by the evaluator
        // (facts were flattened out of a nested `facts` object). Spread first so
        // the structural fields below always win over any stray fact key.
        ...facts,
        product_guid: currentProduct.guid,
        package_guid: selectedPkg ? selectedPkg.guid : undefined,
        dependants: deps,
        evaluation_date: evaluationDate,
        // Ask for the rating trace so the result can explain how each premium
        // was derived (base + folded tables per node).
        _debug: true
      }
    })
  }

  const canSubmit =
    currentProduct &&
    selectedPackageCode &&
    dependants.length > 0 &&
    dependants.every(d => d.date_of_birth)

  const handleClose = () => {
    onClose()
    setSelectedPackageCode('')
    setDependants([newDependant('Principal')])
  }

  if (quotationResults) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <QuotationResults
          results={quotationResults}
          onClose={handleClose}
          productName={currentProduct ? currentProduct.name : undefined}
        />
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ backgroundColor: '#173E4B', color: '#fff' }}>
        Create Quotation
        {currentProduct && (
          <Typography variant="body2" style={{ marginTop: 4, opacity: 0.8 }}>
            Product: {currentProduct.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent style={{ marginTop: 16 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Package</InputLabel>
              <Select
                value={selectedPackageCode}
                onChange={handlePackageChange}
                label="Package"
              >
                {packages.map(pkg => (
                  <MenuItem key={pkg.code} value={pkg.code}>
                    {pkg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Evaluation Date"
              type="date"
              value={evaluationDate}
              onChange={e => setEvaluationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box style={{ marginTop: 24 }}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Dependants
            </Typography>
            <Button
              size="small"
              onClick={addDependant}
              disabled={dependants.length >= MAX_DEPENDANTS}
              style={{ textTransform: 'none', color: '#173E4B' }}
            >
              + Add dependant
            </Button>
          </Box>

          {dependants.map((dep, index) => (
            <Box
              key={index}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                padding: 16,
                marginBottom: 16
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  {index === 0 ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Relationship"
                      value="Principal"
                      disabled
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Relationship</InputLabel>
                      <Select
                        value={dep.relationship}
                        label="Relationship"
                        onChange={e =>
                          updateDependant(index, 'relationship', e.target.value)
                        }
                      >
                        {RELATIONSHIP_OPTIONS.map(rel => (
                          <MenuItem key={rel} value={rel}>
                            {rel.replace(/_/g, ' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Date of Birth"
                    type="date"
                    value={dep.date_of_birth}
                    onChange={e =>
                      updateDependant(index, 'date_of_birth', e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  {index > 0 && (
                    <Button
                      size="small"
                      onClick={() => removeDependant(index)}
                      style={{ textTransform: 'none', color: '#b00020' }}
                    >
                      Remove
                    </Button>
                  )}
                </Grid>
              </Grid>

              {selectedPkg && pkgBenefits.length > 0 && (
                <Box style={{ marginTop: 12 }}>
                  <Typography
                    variant="caption"
                    style={{ fontWeight: 600 }}
                    color="textSecondary"
                  >
                    Benefits
                  </Typography>
                  <FormGroup row>
                    {pkgBenefits.map(benefit => (
                      <FormControlLabel
                        key={benefit.guid}
                        control={
                          <Checkbox
                            size="small"
                            checked={(dep.benefits || []).includes(
                              benefit.guid
                            )}
                            onChange={() =>
                              toggleDependantItem(
                                index,
                                'benefits',
                                benefit.guid
                              )
                            }
                            className={classes.checkbox}
                          />
                        }
                        label={benefit.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              {addons.length > 0 && (
                <Box style={{ marginTop: 8 }}>
                  <Typography
                    variant="caption"
                    style={{ fontWeight: 600 }}
                    color="textSecondary"
                  >
                    Add-ons
                  </Typography>
                  <FormGroup row>
                    {addons.map(addon => (
                      <FormControlLabel
                        key={addon.guid}
                        control={
                          <Checkbox
                            size="small"
                            checked={(dep.add_ons || []).includes(addon.guid)}
                            onChange={() =>
                              toggleDependantItem(index, 'add_ons', addon.guid)
                            }
                            className={classes.checkbox}
                          />
                        }
                        label={addon.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Box style={{ marginTop: 24 }}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8
            }}
          >
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Quote-level facts
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Only needed if this product's pricing references a quote-level
                fact (e.g. a negotiated rate or loan amount) — most products
                don't.
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={addFactRow}
              style={{ textTransform: 'none', color: '#173E4B' }}
            >
              + Add fact
            </Button>
          </Box>

          {factRows.map((row, index) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={index}
              style={{ marginBottom: 8 }}
            >
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Fact name"
                  value={row.key}
                  onChange={e => updateFactRow(index, 'key', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Value"
                  value={row.value}
                  onChange={e => updateFactRow(index, 'value', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  size="small"
                  onClick={() => removeFactRow(index)}
                  style={{ textTransform: 'none', color: '#b00020' }}
                >
                  Remove
                </Button>
              </Grid>
            </Grid>
          ))}
        </Box>

        {quotationError && <ErrorMessage>{quotationError}</ErrorMessage>}
      </DialogContent>
      <DialogActions
        style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 16 }}
      >
        <Button onClick={handleClose} style={{ color: '#666' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || quotationLoader}
          variant="contained"
          className={classes.submitButton}
          startIcon={
            quotationLoader ? (
              <CircularProgress size={20} style={{ color: '#fff' }} />
            ) : null
          }
        >
          {quotationLoader ? 'Evaluating...' : 'Evaluate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuotationDialog
